/**
 * The people in whichever tenant the detail pane is showing.
 *
 * @module components/platform/useTenantUserList
 */

import { useEffect, useState } from "react";

import type { ProgrammePlatformClient, UserSummary } from "@/platform";

/** A tenant's people, tagged with the tenant they were fetched for. */
interface TenantUsersRecord {
  tenantId: string;
  users: readonly UserSummary[];
  error: string | null;
}

const NO_USERS: TenantUsersRecord = { tenantId: "", users: [], error: null };
/** Stable identity, so rendering a stale selection does not churn the table. */
const EMPTY_USERS: readonly UserSummary[] = [];

export interface TenantUserListState {
  users: readonly UserSummary[];
  loading: boolean;
  error: string | null;
  /** Reads the selected tenant's users again after a failed read. */
  reload: () => void;
}

/**
 * @param client - The platform client to read through.
 * @param tenantId - The selected tenant, or "" when nothing is selected.
 * @returns The tenant's users, and whether they have arrived.
 *
 * @example
 * ```tsx
 * const { users, loading, error } = useTenantUserList(client, selectedTenantId);
 * ```
 */
export function useTenantUserList(
  client: ProgrammePlatformClient,
  tenantId: string,
): TenantUserListState {
  // One record rather than three states, tagged with the tenant it was fetched
  // for. Anything holding a different tenant is stale by definition, so the
  // loading and empty states fall out of a comparison instead of needing a
  // synchronous clear every time the selection moves.
  const [record, setRecord] = useState<TenantUsersRecord>(NO_USERS);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!tenantId) {
      return undefined;
    }
    // Ignore anything that resolves after the selection has moved on. Nothing
    // is cleared on the way in: the result carries the tenant it belongs to,
    // and the reader below shows a spinner until that matches the selection,
    // so one tenant's people can never appear under another's.
    let active = true;
    client
      .listTenantUsers(tenantId)
      .then((result) => {
        if (active) {
          setRecord({ tenantId, users: result, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setRecord({
            tenantId,
            users: [],
            error: cause instanceof Error ? cause.message : "Unable to load users for tenant",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [client, tenantId, attempt]);

  const ready = Boolean(tenantId) && record.tenantId === tenantId;
  return {
    users: ready ? record.users : EMPTY_USERS,
    loading: Boolean(tenantId) && !ready,
    error: ready ? record.error : null,
    reload: () => {
      setRecord(NO_USERS);
      setAttempt((current) => current + 1);
    },
  };
}
